import { Form } from 'antd';
import { useEffect, useState } from 'react';
import { DarwinDrawer } from '../Drawer';
import { DarwinButton } from '../Button';
import { DarwinDivider } from '../Divider';
import { FieldRenderer } from './FieldRenderer';
import { groupFieldsIntoRows } from './utils';
import type { FiltersDrawerProps } from './types';
import styles from './index.module.css';
import { useTranslation } from 'react-i18next';

export type { FiltersDrawerProps, FilterField } from './types';

export const FiltersDrawer = <
  T extends Record<string, any> = Record<string, any>
>({
  open,
  onClose,
  title,
  subtitle,
  fields,
  form: formProps,
  actions,
  behavior,
  clearFilters,
}: FiltersDrawerProps<T>) => {
  const [form] = Form.useForm<T>();
  const [internalLoading, setInternalLoading] = useState(false);
  const { t } = useTranslation();

  const {
    clearOnClose = true,
    clearOnApply = false,
    closeOnApply = true,
    clearOnCloseIcon = false,
    clearOnCancel = false,
  } = behavior || {};

  const { initialValues, onValuesChange } = formProps || {};
  const { onApply, isApplyDisabled, ...applyButtonProps } =
    actions?.apply || {};

  const formValues = Form.useWatch([], form) as Partial<T>;
  const rows = groupFieldsIntoRows(fields);
  const applyDisabled = isApplyDisabled
    ? isApplyDisabled(formValues || {})
    : false;

  // Reset form when drawer closes if enabled
  useEffect(() => {
    if (!open && clearOnClose) {
      form.resetFields();
    }
  }, [open, form, clearOnClose]);

  const handleApply = async () => {
    try {
      const values = await form.validateFields();

      setInternalLoading(true);
      await onApply?.(values);

      if (clearOnApply) {
        form.resetFields();
      }

      if (closeOnApply) {
        onClose();
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    } finally {
      setInternalLoading(false);
    }
  };

  const handleCancel = () => {
    if (clearOnCancel) {
      form.resetFields();
      clearFilters?.();
    }
    onClose();
  };

  const handleCloseIcon = () => {
    if (clearOnCloseIcon) {
      form.resetFields();
      clearFilters?.();
    }
    onClose();
  };

  const {
    text: applyText = t('darwin-ui.filters-drawer.apply'),
    loading: applyLoading,
  } = actions?.apply || {};

  const isLoading = applyLoading || internalLoading;

  const {
    text: cancelText = t('darwin-ui.filters-drawer.cancel'),
    ...cancelButtonProps
  } = actions?.cancel || {};

  const handleValuesChange = (changedValues: Partial<T>, allValues: T) => {
    if (onValuesChange) {
      const result = onValuesChange(changedValues, allValues);
      // If onValuesChange returns new values, update the form
      if (result && typeof result === 'object') {
        form.setFieldsValue(result as Partial<T>);
      }
    }
  };

  return (
    <DarwinDrawer
      title={<span className={styles.title}>{title}</span>}
      closable={{ placement: 'end' }}
      open={open}
      onClose={handleCloseIcon}
    >
      <div className={styles.contentContainer}>
        <span className={styles.subtitle}>{subtitle}</span>

        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          className={styles.fieldsContainer}
          onValuesChange={handleValuesChange}
        >
          {rows.map((row, i) => (
            <div key={i} className={styles.fieldRow}>
              {row.map(field => {
                const className =
                  field.span === 2 ? styles.fieldFull : styles.fieldHalf;
                return (
                  <div key={field.name} className={className}>
                    <FieldRenderer field={field} />
                  </div>
                );
              })}
            </div>
          ))}
        </Form>

        <DarwinDivider className={styles.actionsDivider} />

        <div className={styles.actionsContainer}>
          <DarwinButton
            type="default"
            onClick={handleCancel}
            className={styles.actionButton}
            {...cancelButtonProps}
          >
            {cancelText}
          </DarwinButton>

          <DarwinButton
            type="primary"
            onClick={handleApply}
            className={styles.actionButton}
            disabled={applyDisabled || isLoading}
            loading={isLoading}
            {...applyButtonProps}
          >
            {applyText}
          </DarwinButton>
        </div>
      </div>
    </DarwinDrawer>
  );
};
